import React, { useEffect, useRef, useState } from "react";
import {
  Upload, Loader2, FileText, Trash2, AlertTriangle, CheckCircle2, Clock, Sparkles, ExternalLink,
} from "lucide-react";
import api, { API, apiError, openDownload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const pill = {
  valid: { label: "Valid", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200", icon: CheckCircle2 },
  expiring: { label: "Expiring soon", cls: "bg-amber-100 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100", icon: Clock },
  expired: { label: "Expired", cls: "bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-200", icon: AlertTriangle },
  unknown: { label: "Expiry not found", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300", icon: Clock },
};

export default function VendorDocuments({ docTypes, onChange, heading, subheading, emptyHint }) {
  const [docs, setDocs] = useState([]);
  const [health, setHealth] = useState(null);
  const [docType, setDocType] = useState(docTypes?.[0] || "Trade Licence");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = (notify = false) =>
    api.get("/profile/documents").then((r) => {
      setDocs(r.data.documents);
      setHealth(r.data.health);
      if (notify && onChange) onChange();
    }).catch(() => {});

  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    try {
      const { data } = await api.post("/profile/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        data.expiry_date
          ? `${data.doc_type} read by AI — expires ${data.expiry_date}`
          : `${data.doc_type} uploaded — no expiry date found, please add it`
      );
      load(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setExpiry = async (d, expiry_date) => {
    try {
      await api.put(`/profile/documents/${d.id}`, { expiry_date });
      load(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async (d) => {
    try {
      await api.delete(`/profile/documents/${d.id}`);
      toast.success("Document removed");
      load(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-border">
        <div>
          <div className="font-semibold text-slate-900 dark:text-slate-100">{heading || "Compliance Documents"}</div>
          <div className="text-xs text-muted-foreground">
            {subheading || "AI reads each upload and flags expiry. Contractors see a “Documents updated” badge when nothing is expired."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="doc-type-select" className="h-10 w-52 bg-card"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card">
              {(docTypes || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" hidden onChange={onUpload} data-testid="doc-file-input" />
          <Button data-testid="upload-doc-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-10 gap-2 font-semibold">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Reading…" : "Upload"}
          </Button>
        </div>
      </div>

      {health && (health.expired > 0 || health.expiring > 0) && (
        <div data-testid="docs-alert" className="px-6 py-3 bg-amber-50 dark:bg-amber-950/20 border-b border-amber-200 dark:border-amber-900/50 text-xs text-amber-800 dark:text-amber-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {health.expired > 0 && <span>{health.expired} document(s) have expired. </span>}
          {health.expiring > 0 && <span>{health.expiring} expiring within 30 days.</span>}
        </div>
      )}

      <div className="p-6">
        {docs.length === 0 ? (
          <div className="text-center py-10">
            <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {emptyHint || "No documents yet. Upload your trade licence and VAT certificate to earn the verified badge."}
            </p>
          </div>
        ) : (
          <div data-testid="documents-list" className="divide-y divide-border">
            {docs.map((d, i) => {
              const p = pill[d.status] || pill.unknown;
              const Icon = p.icon;
              return (
                <div key={d.id} data-testid={`doc-row-${i}`} className="py-4 first:pt-0 last:pb-0 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">{d.doc_type}</span>
                      <span data-testid={`doc-status-${i}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${p.cls}`}>
                        <Icon className="w-3 h-3" /> {p.label}
                        {d.days_left != null && d.status !== "expired" && ` · ${d.days_left}d`}
                      </span>
                      {d.ai_read && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-primary font-bold">
                          <Sparkles className="w-3 h-3" /> AI read
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                      {d.filename}
                      {d.document_number ? ` · No. ${d.document_number}` : ""}
                      {d.issuing_authority ? ` · ${d.issuing_authority}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Input
                      data-testid={`doc-expiry-${i}`}
                      type="date"
                      value={d.expiry_date || ""}
                      onChange={(e) => setExpiry(d, e.target.value)}
                      className="h-9 w-40 text-sm"
                    />
                    <button
                      type="button"
                      data-testid={`doc-view-${i}`}
                      onClick={() => openDownload(`/profile/documents/${d.id}/file`)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                      title="View document"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`doc-delete-${i}`}
                      onClick={() => remove(d)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
